import { Badge, Button, Card, CardHeader, EmptyState, Skeleton, useToast } from "@helmr/ui";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getPlan, issueBill, previewBill, renewPlan, upgradePlan, type Cart, type Issued, type Preview, type Tender } from "../../api/billing";
import { fetchSellable, type SellableItem } from "../../api/catalogue";
import { isApiError } from "../../api/client";
import { searchMembers } from "../../api/lists";
import { NoAccess } from "../../app/NoAccess";
import { today } from "../../lib/dates";
import { FailureBody } from "../home/states";
import { discountCapBp, useAppContext, useDebounced, useIdempotentKey, useMoney, useScope } from "./hooks";
import { fractionDigitsOf, parseMoneyInput, parsePercentToBp } from "./money";
import { PageHead } from "./states";
import { billing as t } from "./strings";
import s from "./billing.module.css";

interface CartRow { itemId: string; name: string; qty: number; discountText: string }
interface PayRow { id: number; method: Tender; full: boolean; text: string }

/** Time between automatic retries when the connection drops during Issue. The key and body stay the same. */
export const RETRY_DELAY_MS = 1500;
const MAX_RETRIES = 3;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function CounterPage() {
  const ctx = useAppContext();
  const { client, now, scopeKey, branchId: sessionBranch } = useScope();
  const { fmt, currency, locale } = useMoney(ctx);
  const nav = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const planId = sp.get("planId");
  // A plan action needs a plan: with no planId this is an ordinary bill, whatever `action` says.
  const action = !planId ? null : sp.get("action") === "upgrade" ? "upgrade" : "renew";
  const branchId = ctx.me?.branch?.id ?? sessionBranch ?? "";

  const [member, setMember] = useState<{ id: string; name: string } | null>(null);
  const [rows, setRows] = useState<CartRow[]>([]);
  const [coupon, setCoupon] = useState("");
  const [notes, setNotes] = useState("");
  const [pays, setPays] = useState<PayRow[]>([]);
  const [filter, setFilter] = useState("");
  const [phase, setPhase] = useState<"idle" | "issuing" | "checking">("idle");
  const [issueError, setIssueError] = useState<unknown>(null);
  const busy = useRef(false);
  const idem = useIdempotentKey();
  const prefilled = useRef(false);
  const prefilledRow = useRef(false);

  const items = useQuery({ queryKey: ["sellable", ...scopeKey, branchId], queryFn: () => fetchSellable(client, branchId), enabled: !!branchId && ctx.has("billing.create_bill"), retry: false });
  const plan = useQuery({ queryKey: ["plan", ...scopeKey, planId], queryFn: () => getPlan(client, planId!), enabled: !!planId, retry: false });

  // Renew / upgrade arrive prefilled from the member's plan (app-members passes planId and action).
  useEffect(() => {
    if (prefilled.current) return;
    const memberId = sp.get("memberId") ?? plan.data?.memberId;
    if (planId && !plan.data) return;
    if (memberId && !member) {
      prefilled.current = true;
      void client.get<{ member?: { name?: string }; name?: string }>(`/members/${memberId}`).then((r) => setMember({ id: memberId, name: r.member?.name ?? r.name ?? memberId }), () => setMember({ id: memberId, name: memberId }));
    }
  }, [sp, plan.data, planId, member, client]);
  useEffect(() => {
    if (prefilledRow.current || action !== "renew" || !plan.data || !items.data) return;
    prefilledRow.current = true;
    const it = items.data.find((i) => i.id === plan.data!.itemId);
    if (it) setRows([{ itemId: it.id, name: it.name, qty: 1, discountText: "" }]);
  }, [action, plan.data, items.data]);

  const cap = discountCapBp(ctx);
  const rowState = rows.map((r) => {
    const bp = r.discountText.trim() === "" ? null : parsePercentToBp(r.discountText);
    const error = r.discountText.trim() === "" ? null : bp === null ? t.counter.discountBad : bp > cap ? t.counter.discountOver(String(cap / 100)) : null;
    return { bp, error };
  });
  const rowsValid = rows.length > 0 && rowState.every((r) => r.error === null);

  const digits = fractionDigitsOf(currency, locale);
  const cartBase: Omit<Cart, "payments"> | null = member && rowsValid && branchId ? {
    memberId: member.id, branchId,
    lines: rows.map((r, i) => ({ itemId: r.itemId, quantity: r.qty, ...(rowState[i]!.bp ? { discount: { type: "percent" as const, bp: rowState[i]!.bp! } } : {}) })),
    ...(coupon.trim() ? { couponCode: coupon.trim() } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}),
  } : null;
  const previewKey = cartBase ? JSON.stringify(cartBase) : "";
  const debounced = useDebounced(previewKey, 300);
  const wantPreview = action !== "upgrade" && debounced !== "";
  const preview = useQuery<Preview>({
    queryKey: ["bill-preview", ...scopeKey, debounced],
    queryFn: ({ signal }) => previewBill(client, JSON.parse(debounced) as Cart, signal),
    enabled: wantPreview, retry: false, placeholderData: keepPreviousData, staleTime: 0, gcTime: 0,
  });
  const pv = preview.data;
  const stale = action === "upgrade" ? false : previewKey !== "" && (previewKey !== debounced || preview.isFetching || !pv);

  // A free bill has nothing to tender, and the API refuses a payment of 0.
  const payBody = pays.map((p) => ({ method: p.method, amount: p.full ? pv?.bill.total ?? 0 : parseMoneyInput(p.text, digits) ?? 0 })).filter((p) => p.amount > 0);
  const payInvalid = pays.some((p) => (p.full ? !pv : parseMoneyInput(p.text, digits) === null));

  const planReady = !planId || !!plan.data; // never let a plan action fall through to a plain bill
  const canIssue = !!cartBase && planReady && !stale && !payInvalid && phase === "idle" && (action === "upgrade" ? rows.length === 1 && ctx.has("billing.upgrade") : !!pv && pv.bill.lines.length > 0 && (action !== "renew" || ctx.has("billing.renew")));

  async function issue() {
    if (busy.current || !cartBase || !planReady) return;
    busy.current = true;
    let issued = false;
    setIssueError(null);
    setPhase("issuing");
    const payments = payBody.length ? payBody : undefined;
    const cart: Cart = { ...cartBase, ...(payments ? { payments } : {}) };
    const extras = { ...(coupon.trim() ? { couponCode: coupon.trim() } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}), ...(cartBase.lines[0]?.discount ? { discount: cartBase.lines[0].discount } : {}), ...(payments ? { payments } : {}) };
    const send = (key: string): Promise<Issued> =>
      action === "renew" && plan.data ? renewPlan(client, plan.data.id, { expectedVersion: plan.data.version, itemId: rows[0]!.itemId, branchId, cart: extras }, key)
      : action === "upgrade" && plan.data ? upgradePlan(client, plan.data.id, { expectedVersion: plan.data.version, newItemId: rows[0]!.itemId, asOf: today(now(), ctx.timeZone), cart: extras }, key)
      : issueBill(client, cart, key);
    const fingerprint = JSON.stringify([action, planId, cart.lines, extras]);
    try {
      for (let attempt = 0; ; attempt++) {
        const key = idem.forBody(fingerprint);
        try {
          const res = await send(key);
          issued = true; // stays locked until the page goes: the key is spent, a late click would raise a second bill
          idem.renew();
          void qc.invalidateQueries({ queryKey: ["bills"] });
          toast.show(t.counter.issued);
          nav(`/billing/bills/${res.bill.id}`);
          return;
        } catch (e) {
          if (isApiError(e) && e.isOffline && attempt < MAX_RETRIES) {
            idem.setPending(true);
            setPhase("checking");
            await sleep(RETRY_DELAY_MS);
            continue;
          }
          idem.setPending(isApiError(e) && e.isOffline);
          setIssueError(e);
          return;
        }
      }
    } finally {
      if (!issued) {
        busy.current = false;
        setPhase("idle");
      }
    }
  }

  // The item list is GET /catalogue/sellable (`billing.create_bill`); renew and upgrade add their own lever at issue.
  if (ctx.status !== "loading" && !ctx.has("billing.create_bill")) return <NoAccess />;
  const title = action === "upgrade" ? t.counter.upgradeTitle : action === "renew" ? t.counter.renewTitle : t.counter.title;
  const visibleItems = (items.data ?? []).filter((i) => i.name.toLowerCase().includes(filter.toLowerCase()));
  const addItem = (it: SellableItem) => setRows((rs) => {
    if (action) return [{ itemId: it.id, name: it.name, qty: 1, discountText: "" }]; // renew and upgrade bill one line, quantity 1
    const at = rs.findIndex((r) => r.itemId === it.id);
    return at >= 0 ? rs.map((r, i) => (i === at ? { ...r, qty: r.qty + 1 } : r)) : [...rs, { itemId: it.id, name: it.name, qty: 1, discountText: "" }];
  });
  const patch = (i: number, p: Partial<CartRow>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...p } : r)));

  return (
    <div className={s.page}>
      <title>{title} · Helmr</title>
      <PageHead title={title} action={<Button as={Link} to="/billing">{t.bill.back}</Button>} />
      {planId && plan.error ? <Card><FailureBody error={plan.error} title={t.counter.planLoadError} what="the plan" onRetry={() => void plan.refetch()} /></Card> : null}
      <div className={s.counter}>
        <div className={s.page}>
          <MemberCard member={member} onPick={setMember} lockedByPlan={!!planId} />
          <Card>
            <CardHeader title={t.counter.items} subtitle={action === "upgrade" ? t.counter.upgradePick : undefined} />
            <div className={s.pad} style={{ paddingBottom: 0 }}>
              <input className={s.search} aria-label={t.counter.itemSearch} placeholder={t.counter.itemSearch} value={filter} onChange={(e) => setFilter(e.target.value)} />
            </div>
            {items.isPending && !!branchId ? <div className={s.skel}><Skeleton height={56} /><Skeleton height={56} /></div>
              : items.error ? <FailureBody error={items.error} title="Could not load items" what="the catalogue" onRetry={() => void items.refetch()} />
              : visibleItems.length === 0 ? <EmptyState compact icon="receipt" title={t.counter.itemsEmpty} />
              : (
                <div className={s.items}>
                  {visibleItems.map((it) => (
                    <button key={it.id} type="button" className={s.item} onClick={() => addItem(it)} aria-label={`Add ${it.name}`}>
                      <span>{it.name}</span>
                      <span className={s.muted}>{fmt(it.price)}{it.priceIncludesTax ? " incl. tax" : " + tax"}</span>
                    </button>
                  ))}
                </div>
              )}
          </Card>
        </div>

        <Card aria-label={t.counter.cart}>
          <CardHeader title={t.counter.cart} />
          {rows.length === 0 ? <EmptyState compact icon="bill" title={t.counter.cartEmpty} /> : (
            <div className={s.cartLines}>
              {rows.map((r, i) => (
                <div key={r.itemId} className={s.cline}>
                  <span className={s.nm}>{r.name}</span>
                  <Button size="sm" variant="plain" aria-label={t.counter.remove(r.name)} onClick={() => setRows((rs) => rs.filter((_, k) => k !== i))}>✕</Button>
                  <div className={s.ctl}>
                    {action ? null : (
                      <input type="number" min={1} max={1000} step={1} aria-label={t.counter.qty(r.name)} value={r.qty} onChange={(e) => patch(i, { qty: Math.max(1, Math.min(1000, Math.trunc(Number(e.target.value)) || 1)) })} />
                    )}
                    <label className={s.field} style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>{t.counter.discount}
                      <input inputMode="decimal" aria-label={`${t.counter.discount}: ${r.name}`} value={r.discountText} onChange={(e) => patch(i, { discountText: e.target.value })} aria-invalid={rowState[i]?.error ? true : undefined} />
                    </label>
                    <span className={s.muted}>{t.counter.discountCap(String(cap / 100))}</span>
                  </div>
                  {rowState[i]?.error ? <span className={s.err} role="alert" style={{ gridColumn: "1 / -1" }}>{rowState[i]!.error}</span> : null}
                </div>
              ))}
            </div>
          )}
          <div className={s.pad}>
            <label className={s.field}>{t.counter.coupon}<input value={coupon} onChange={(e) => setCoupon(e.target.value)} /></label>
            <label className={s.field}>{t.counter.notes}<input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
          </div>

          <PreviewPanel action={action} preview={pv} pending={wantPreview && preview.isPending} error={preview.error} stale={stale} fmt={fmt} onRetry={() => void preview.refetch()} hasRows={rows.length > 0} />

          {ctx.has("billing.collect_payment") && action !== "upgrade" ? (
            <div className={s.pad}>
              <strong>{t.counter.payNow}</strong>
              {pays.map((p) => (
                <div key={p.id} className={s.payrow}>
                  <label className={s.field}>{t.counter.tender}
                    <select value={p.method} onChange={(e) => setPays((ps) => ps.map((x) => (x.id === p.id ? { ...x, method: e.target.value as Tender } : x)))}>
                      <option value="cash">{t.methods.cash}</option>
                      <option value="member_credit">{t.methods.member_credit}</option>
                    </select>
                  </label>
                  {p.full ? (
                    <Badge tone="info">{pv ? `${t.counter.payFull}: ${fmt(pv.bill.total)}` : t.counter.payFull}</Badge>
                  ) : (
                    <label className={s.field}>{t.counter.payAmount}
                      <input inputMode="decimal" value={p.text} aria-invalid={p.text !== "" && parseMoneyInput(p.text, digits) === null ? true : undefined} onChange={(e) => setPays((ps) => ps.map((x) => (x.id === p.id ? { ...x, text: e.target.value } : x)))} />
                    </label>
                  )}
                  <Button size="sm" onClick={() => setPays((ps) => ps.filter((x) => x.id !== p.id))}>Remove</Button>
                  {!p.full && p.text !== "" && parseMoneyInput(p.text, digits) === null ? <span className={s.err} role="alert">{t.counter.payBad}</span> : null}
                </div>
              ))}
              <div className={s.payrow}>
                {pays.length === 0 ? <Button size="sm" disabled={!pv} onClick={() => setPays([{ id: 1, method: "cash", full: true, text: "" }])}>{t.counter.payFull}</Button> : null}
                <Button size="sm" onClick={() => setPays((ps) => [...ps, { id: (ps.at(-1)?.id ?? 0) + 1, method: "cash", full: false, text: "" }])}>{t.counter.addPayment}</Button>
              </div>
            </div>
          ) : null}

          {phase === "checking" ? <p className={s.notice} role="status">{t.counter.checkingBody}</p> : null}
          {issueError ? <div role="alert" className={s.notice} style={{ color: "var(--color-signal-bad)" }}>{isApiError(issueError) ? issueError.message : t.errors.generic}{isApiError(issueError) && issueError.requestId ? ` (request ${issueError.requestId})` : ""}</div> : null}
          {action === "upgrade" && !ctx.has("billing.upgrade") ? <p className={s.notice}>{t.counter.upgradeLever}</p> : null}
          {action === "renew" && !ctx.has("billing.renew") ? <p className={s.notice}>{t.counter.renewLever}</p> : null}
          <div className={s.actions}>
            <Button variant="primary" disabled={!canIssue} busy={phase !== "idle"} busyLabel={phase === "checking" ? t.counter.checking : t.counter.issuing} onClick={() => void issue()}>
              {action === "upgrade" ? t.counter.upgradeConfirm : t.counter.issue}
            </Button>
            {stale && rows.length > 0 && action !== "upgrade" ? <span className={s.muted} role="status">{t.counter.stale}</span> : null}
          </div>
        </Card>
      </div>
    </div>
  );
}

function PreviewPanel({ action, preview, pending, error, stale, fmt, onRetry, hasRows }: {
  action: string | null; preview: Preview | undefined; pending: boolean; error: unknown; stale: boolean; fmt: (n: number) => string; onRetry: () => void; hasRows: boolean;
}) {
  if (action === "upgrade") return hasRows ? <p className={s.notice}>{t.counter.upgradeNoPreview}</p> : null;
  if (!hasRows) return null;
  if (error && !preview) return <FailureBody error={error} title={t.counter.previewError} what="the price" onRetry={onRetry} />;
  if (pending || !preview) return <div className={s.skel} aria-busy="true" aria-label={t.counter.updating}><Skeleton height={20} /><Skeleton height={20} /><Skeleton height={28} /></div>;
  const b = preview.bill;
  return (
    <div className={s.totals} aria-busy={stale || undefined} style={stale ? { opacity: 0.6 } : undefined} data-testid="preview">
      {b.lines.map((l) => (
        <div key={l.id} style={l.parentLineId ? { paddingLeft: 12 } : undefined}>
          <div className={s.trow}><span className={s.wrap2}>{l.name} × {l.quantity}</span><span className={s.num}>{fmt(l.lineTotal)}</span></div>
          <div className={s.muted} style={{ font: "var(--type-caption)" }}>
            {l.taxRateBp > 0 ? `${l.cgst > 0 ? `CGST ${fmt(l.cgst)} · SGST ${fmt(l.sgst)}` : ""}${l.igst > 0 ? `IGST ${fmt(l.igst)}` : ""}` : ""}
          </div>
        </div>
      ))}
      <div className={s.trow}><span>{t.counter.summary.subtotal}</span><span className={s.num}>{fmt(b.subtotalExTax)}</span></div>
      {b.discountTotal > 0 ? <div className={s.trow}><span>{t.counter.summary.discount}</span><span className={s.num}>{fmt(b.discountTotal)}</span></div> : null}
      <div className={s.trow}><span>{t.counter.summary.tax}</span><span className={s.num}>{fmt(b.taxTotal)}</span></div>
      {b.roundOff !== 0 ? <div className={s.trow}><span>{t.counter.summary.round}</span><span className={s.num}>{fmt(b.roundOff)}</span></div> : null}
      <div className={`${s.trow} ${s.grand}`}><span>{t.counter.summary.total}</span><span className={s.num} data-testid="preview-total">{fmt(b.total)}</span></div>
    </div>
  );
}

function MemberCard({ member, onPick, lockedByPlan }: { member: { id: string; name: string } | null; onPick: (m: { id: string; name: string } | null) => void; lockedByPlan: boolean }) {
  const { client } = useScope();
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim(), 250);
  const hits = useQuery({ queryKey: ["member-pick", dq], queryFn: ({ signal }) => searchMembers(client, dq, signal), enabled: !member && dq.length >= 2, retry: false });
  return (
    <Card>
      <CardHeader title={t.counter.member} action={member && !lockedByPlan ? <Button size="sm" onClick={() => onPick(null)}>{t.counter.change}</Button> : undefined} />
      <div className={s.pad}>
        {member ? <strong>{member.name}</strong> : (
          <>
            <input className={s.search} aria-label={t.counter.memberSearch} placeholder={t.counter.memberSearch} value={q} onChange={(e) => setQ(e.target.value)} />
            {hits.isFetching ? <Skeleton height={34} /> : null}
            {hits.error ? <FailureBody error={hits.error} title="Could not search members" what="members" onRetry={() => void hits.refetch()} /> : null}
            {hits.data && hits.data.length === 0 ? <span className={s.muted}>{t.counter.memberNone}</span> : null}
            {hits.data?.map((m) => (
              <button key={m.id} type="button" className={s.item} onClick={() => onPick({ id: m.id, name: m.name })}><span>{m.name}</span><span className={s.muted}>{m.phone ?? ""}</span></button>
            ))}
          </>
        )}
      </div>
    </Card>
  );
}
